import { ArrowForward } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import CButton from '../../components/CButton';
import CLocalizedText from '../../components/CLocalizedText';
import CLocalizedTextStringStatic from '../../components/CLocalizedTextStringStatic';
import CPageModule from '../../components/CPageModule';
import CRadioGroup from '../../components/CRadioGroup';
import CRequestSuccessPage from '../../components/CRequestSuccessPage';
import CustomTextField from '../../components/CustomTextField';
import PageModuleFormGroup from '../../components/PageModuleFormGroup';
import PageModuleFormRow from '../../components/PageModuleFormRow';
import XDCheckbox from '../../components/forms/XDCheckbox';
import Page from '../../components/layout/Page';
import { CAccountRequestRoles } from '../../constants/CAccountRequestRoles';
import { type EUserRoles } from '../../enums/EUserRoles';
import { updateStateAttribute } from '../../helpers/helper';
import { useAppDispatch } from '../../hooks/hooks';
import type { IRequestState } from '../../models/IRequestState';
import { LocaleEnum, type TLocales } from '../../models/TLocales';
import { SvConfigServices } from '../../services/SvConfigServices';
import UserManagementCoreService from '../../services/userManagementCoreService';
import { updateLocale, updateSnackbar } from '../../store/appSlice';

interface FormModel {
  firstName: string;
  lastName: string;
  company: string;
  position?: string;
  email: string;
  requestedRole: EUserRoles;
  termsAccepted: boolean;
  subscribeToMailingList: boolean;
}

interface State {
  reqState: IRequestState;
  form: Partial<FormModel>;
  selectedRoleOption: keyof typeof CAccountRequestRoles | null;
  userSignUpSuccess: boolean;
  redirectURL: string;
}

export default function PSignUpIndexPage() {
  const [state, setState] = useState<State>({
    form: {},
    selectedRoleOption: null,
    reqState: { isError: false, isLoading: false },
    userSignUpSuccess: false,
    redirectURL: '/'
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const signUpTitle = CLocalizedTextStringStatic('signUp.title');

  const handleFormChange = (e: React.ChangeEvent, partialForm: Partial<FormModel>) => {
    updateStateAttribute<State>(
      {
        form: {
          ...state.form,
          ...partialForm
        }
      },
      setState
    );
  };

  const handleFormSubmit = async () => {
    updateStateAttribute<State>({ reqState: { isLoading: true, isError: false } }, setState);

    const res = await UserManagementCoreService.userSignup(state.form);
    updateStateAttribute<State>(
      {
        reqState: {
          isLoading: false,
          isError: !(await res.isSuccess()),
          strError: await res.getMessage(),
          objError: await res.getDetail()
        },
        userSignUpSuccess: await res.isSuccess()
      },
      setState
    );

    if (!(await res.isSuccess())) {
      dispatch(
        updateSnackbar({
          message: await res.getMessage(),
          severity: 'error',
          isVisible: true
        })
      );
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      await handleFormSubmit();
    }
  };

  const successTitle = CLocalizedTextStringStatic('user.sign-up.success.heading');
  const successDesc = CLocalizedTextStringStatic('user.sign-up.success.subtext');

  useEffect(() => {
    (async () => {
      const processedSearchParam = new URLSearchParams(searchParams);
      const requestedLocale = processedSearchParam.get('locale');

      if (!requestedLocale) return;

      const mapLocale = (locale?: string): TLocales | undefined => {
        if (!locale) return undefined;

        for (const localeIterator of Object.values(LocaleEnum)) {
          if (localeIterator.toLowerCase() === locale) return localeIterator;
        }

        return undefined;
      };

      const locale = mapLocale(requestedLocale);

      if (!locale) return;

      dispatch(updateLocale({ locale }));

      updateStateAttribute<State>(
        { redirectURL: (await (await SvConfigServices.getConfig()).getData())?.homepageURL },
        setState
      );

      processedSearchParam.delete('locale');
      setSearchParams(processedSearchParam);
    })();
  }, []);

  return (
    <>
      {state.userSignUpSuccess && (
        <CRequestSuccessPage heading={successTitle} description={successDesc} redirectURL={state.redirectURL} />
      )}
      {!state.userSignUpSuccess && (
        <Page title={signUpTitle}>
          <CPageModule>
            <h1>
              <CLocalizedText dictKey={'signUp.requestAccessButton'} />
            </h1>
            <div>
              <p>
                <CLocalizedText dictKey={'user.sign-up.subHeading1'} />
              </p>
            </div>
            <PageModuleFormGroup>
              <h3>
                <CLocalizedText dictKey={'signUp.personalDetails'} />
              </h3>
              <PageModuleFormRow>
                <CustomTextField
                  label={<CLocalizedText dictKey={'textField.label.firstName'} />}
                  value={state.form.firstName ?? ''}
                  error={(state.reqState.objError?.firstName?.length ?? 0) > 0}
                  helperText={state.reqState.objError?.firstName}
                  onChange={(e) => {
                    handleFormChange(e, { firstName: e.target.value });
                  }}
                  onKeyDown={handleKeyDown}
                  required
                />
              </PageModuleFormRow>

              <PageModuleFormRow>
                <CustomTextField
                  label={<CLocalizedText dictKey={'textField.label.lastName'} />}
                  value={state.form.lastName ?? ''}
                  error={(state.reqState.objError?.lastName?.length ?? 0) > 0}
                  helperText={state.reqState.objError?.lastName}
                  onChange={(e) => {
                    handleFormChange(e, { lastName: e.target.value });
                  }}
                  onKeyDown={handleKeyDown}
                  required
                />
              </PageModuleFormRow>

              <PageModuleFormRow description={<CLocalizedText dictKey={'textField.label.emailDescription'} />}>
                <CustomTextField
                  label={<CLocalizedText dictKey={'textField.label.email'} />}
                  type={'email'}
                  value={state.form.email ?? ''}
                  error={(state.reqState.objError?.email?.length ?? 0) > 0}
                  helperText={state.reqState.objError?.email}
                  onChange={(e) => {
                    handleFormChange(e, { email: e.target.value.trim() });
                  }}
                  onKeyDown={handleKeyDown}
                  required
                />
              </PageModuleFormRow>

              <PageModuleFormRow>
                <CustomTextField
                  label={<CLocalizedText dictKey={'textField.label.company'} />}
                  value={state.form.company ?? ''}
                  onChange={(e) => {
                    handleFormChange(e, { company: e.target.value });
                  }}
                  onKeyDown={handleKeyDown}
                  error={(state.reqState.objError?.company?.length ?? 0) > 0}
                  helperText={state.reqState.objError?.company?.[0]}
                  required
                />
              </PageModuleFormRow>

              <PageModuleFormRow>
                <CustomTextField
                  label={<CLocalizedText dictKey={'textField.label.position'} />}
                  value={state.form.position ?? ''}
                  onChange={(e) => {
                    handleFormChange(e, { position: e.target.value });
                  }}
                  onKeyDown={handleKeyDown}
                />
              </PageModuleFormRow>
            </PageModuleFormGroup>

            <h3>
              <CLocalizedText dictKey={'pubFs.registration.subHeading2'} />
            </h3>

            <div>
              <p>
                <CLocalizedText dictKey={'pubFs.registration.subHeading2.text1'} />
              </p>
            </div>

            <PageModuleFormGroup>
              <PageModuleFormRow>
                <CRadioGroup
                  options={CAccountRequestRoles}
                  value={state.selectedRoleOption}
                  error={(state.reqState.objError?.requestedRole?.length ?? 0) > 0}
                  helperText={state.reqState.objError?.requestedRole}
                  onChange={(key, value) => {
                    updateStateAttribute<State>(
                      {
                        selectedRoleOption: key,
                        form: {
                          ...state.form,
                          requestedRole: value
                        }
                      },
                      setState
                    );
                  }}
                />
              </PageModuleFormRow>
            </PageModuleFormGroup>

            <h3>
              <CLocalizedText dictKey={'pubFs.registration.subHeading3'} />
            </h3>

            <PageModuleFormGroup>
              <PageModuleFormRow>
                <XDCheckbox
                  checked={state.form.termsAccepted}
                  onChange={(e) => {
                    handleFormChange(e, { termsAccepted: e.target.checked });
                  }}
                  hasHyperlinks={true}
                  error={(state.reqState.objError?.termsAccepted?.length ?? 0) > 0}
                  helperText={state.reqState.objError?.termsAccepted}
                >
                  <CLocalizedText dictKey={'pubFs.registration.terms1'} />
                </XDCheckbox>
              </PageModuleFormRow>

              <PageModuleFormRow>
                <XDCheckbox
                  checked={state.form.subscribeToMailingList}
                  onChange={(e) => {
                    handleFormChange(e, { subscribeToMailingList: e.target.checked });
                  }}
                >
                  <CLocalizedText dictKey={'pubFs.registration.terms2'} />
                </XDCheckbox>
              </PageModuleFormRow>
            </PageModuleFormGroup>
            <PageModuleFormRow>
              <CButton
                isLoading={state.reqState.isLoading}
                icons={{ end: <ArrowForward /> }}
                onClick={handleFormSubmit}
                style={{ borderWidth: '1px', width: '100%', justifyContent: 'center' }}
                isForm
              >
                <CLocalizedText dictKey={'signUp.requestAccessButton'} />
              </CButton>
            </PageModuleFormRow>
          </CPageModule>
        </Page>
      )}
    </>
  );
}
